import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, Empty, ErrorBox, Loading, Panel, Stat, Stats } from '../components/ui'
import { api } from '../lib/api'
import { dayAndTime, gbp, titleise } from '../lib/format'
import type { AppSettings, Client, Overview as OverviewData, Room } from '../types'

export function Overview() {
  const overview = useQuery({ queryKey: ['overview'], queryFn: () => api.get<OverviewData>('/overview') })
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => api.get<AppSettings>('/settings') })
  const rooms = useQuery({ queryKey: ['rooms'], queryFn: () => api.get<Room[]>('/rooms') })
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') })

  if (overview.isLoading) return <><PageBar title="Overview" /><Content><Loading /></Content></>
  if (overview.error) return <><PageBar title="Overview" /><Content><ErrorBox error={overview.error} /></Content></>

  const data = overview.data!
  const sla = settings.data?.sla_hours ?? 4
  const roomName = (id: number) => rooms.data?.find((r) => r.id === id)?.name ?? ''
  const clientName = (id: number | null) =>
    id === null ? null : (clients.data?.find((c) => c.id === id)?.name ?? null)

  const segmentTotal = Object.values(data.segments).reduce((a, b) => a + b, 0)
  // A dashboard of zeros reads as a broken app - the same failure as the silence alarm
  // announcing "no enquiries recorded at all" over 453 of them. Explain it while it is
  // true, and get out of the way the moment there is anything real to show.
  const starting = data.history > 0 && data.client_30d === 0 && data.booked_30d_pence === 0

  return (
    <>
      <PageBar title="Overview" />
      <Content>
        {!data.silence.ok && (
          <div className="alarm">
            <b>Silence alarm.</b> {data.silence.reason}. Nothing has arrived through the form or
            inbox — in 2024 this went unnoticed for five months.
          </div>
        )}

        {data.late.length > 0 && (
          <div className="alarm warn">
            <b>
              {data.late.length} enquir{data.late.length === 1 ? 'y' : 'ies'} past the {sla}h reply
              target.
            </b>{' '}
            {data.late.slice(0, 3).map((e, i) => (
              <span key={e.id}>
                {i > 0 && ' · '}
                <Link to={`/enquiries/${e.id}`}>{e.name || e.email}</Link>
              </span>
            ))}
          </div>
        )}

        {starting && (
          <div className="note">
            <b>These numbers start from the day the form was wired up</b>, not from the
            beginning. The {data.history} enquiries imported from the 2021–2026 export are
            archived and deliberately not counted here: nothing in that export records
            whether anyone ever replied, so counting them would put a confidently wrong
            number on this page. The people are not lost —{' '}
            <Link to="/clients?tab=contacts">{data.contacts} of them are under Contacts</Link>.
            Bookings and invoices are empty because the studio&rsquo;s history lives in a
            spreadsheet; this page fills up as work goes through the app.
          </div>
        )}

        <Stats>
          <Stat label="Client enquiries · 30d" value={data.client_30d} note="Genuine booking enquiries" />
          <Stat
            label="Waiting for a reply"
            value={data.waiting}
            tone={data.waiting ? 'alert' : 'ok'}
            note={data.waiting ? 'Nobody has answered these' : 'All answered'}
          />
          <Stat
            label="Avg first reply"
            value={
              data.avg_reply_hours === null ? '–' : (
                <>{data.avg_reply_hours.toFixed(1)}<span className="unit">h</span></>
              )
            }
            tone={
              data.avg_reply_hours === null ? 'muted' : data.avg_reply_hours > sla ? 'alert' : 'ok'
            }
            note={data.avg_reply_hours === null ? 'No replies sent from here yet' : `Target ${sla}h`}
          />
          <Stat
            label="Enquiry → booking"
            value={
              data.conversion_pct === null ? '–' : (
                <>{Math.round(data.conversion_pct)}<span className="unit">%</span></>
              )
            }
            tone={data.conversion_pct === null ? 'muted' : undefined}
            note={data.conversion_pct === null ? 'Needs live enquiries' : 'Of client enquiries'}
          />
        </Stats>

        <Stats>
          <Stat label="Booked · 30d" value={gbp(data.booked_30d_pence)} note="Quoted value of sessions" />
          <Stat label="Invoiced & paid · 30d" value={gbp(data.paid_30d_pence)} tone="ok" note="Settled" />
          <Stat
            label="Outstanding"
            value={gbp(data.outstanding_pence)}
            tone={data.outstanding_pence ? 'alert' : undefined}
            note="Unpaid invoices"
          />
          <Stat
            label="Receiving"
            value={<span style={{ fontSize: 26 }}>{data.silence.ok ? 'OK' : 'QUIET'}</span>}
            tone={data.silence.ok ? 'ok' : 'alert'}
            note={
              data.silence.days_quiet === null
                ? 'No data'
                : `Last message ${data.silence.days_quiet}d ago`
            }
          />
        </Stats>

        <div className="cols">
          <section>
            <h2 className="sec">Next sessions</h2>
            <Panel>
              {data.upcoming.length ? (
                <table className="cards">
                  <thead>
                    <tr><th>When</th><th>Who</th><th>Room</th><th>Value</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {data.upcoming.map((b) => (
                      <tr key={b.id}>
                        <td className="when"><Link to={`/bookings/${b.id}`}>{dayAndTime(b.starts_at)}</Link></td>
                        <td>{clientName(b.client_id) ?? <span className="muted">—</span>}</td>
                        <td className="muted" data-label="Room">{roomName(b.room_id)}</td>
                        <td data-label="Value">{gbp(b.quoted_pence)}</td>
                        <td><Badge kind="b" value={b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Empty>Nothing booked yet.</Empty>
              )}
            </Panel>
          </section>

          <section>
            <h2 className="sec">Who&rsquo;s asking</h2>
            <Panel pad>
              {segmentTotal ? (
                Object.entries(data.segments).map(([segment, count]) => (
                  <div className="segrow" key={segment}>
                    <Badge kind="seg" value={segment} />
                    <div className="bar">
                      <span style={{ width: `${Math.round((count / segmentTotal) * 100)}%` }} />
                    </div>
                    <b>{count}</b>
                  </div>
                ))
              ) : (
                <Empty>No live client enquiries yet.</Empty>
              )}
              <p className="hint">
                Segments drive which reply goes out — a hobbyist, an agency and a student each get a
                different first response.
              </p>
            </Panel>
          </section>
        </div>

        {settings.data && (
          <p className="hint">
            Email backend: <b>{settings.data.email_backend}</b> · Drafting:{' '}
            <b>{titleise(settings.data.drafting)}</b> · VAT:{' '}
            <b>{settings.data.vat_registered ? `${settings.data.vat_rate_pct}%` : 'off'}</b>
          </p>
        )}
      </Content>
    </>
  )
}
